import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {DiffUser} from '@github-ui/diff-lines/types'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {
  useActiveChangeGroup,
  useDiffEntries,
  useDiffEntriesWithFilter,
  useDiffSummaries,
  useDiffSummaryCount,
  useProgressiveDiffActions,
} from '../stores/selectors'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {useWindowVirtualizer} from '@tanstack/react-virtual'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {PullRequestDiffsAnimation} from './PullRequestDiffsAnimation'
import {scheduleLowPriorityUpdate} from '../utils/schedule-low-priority-update'
import {VirtualizedDiffRow} from './PullRequestVirtualizedDiffRow'

export interface PullRequestVirtualizedDiffsListProps {
  basePath: string
  currentUser: DiffUser
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export function PullRequestVirtualizedDiffsList({
  basePath,
  currentUser,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestVirtualizedDiffsListProps) {
  'use no memo'

  const diffsParentRef = useRef<HTMLDivElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const virtualDiffEntries = useDiffEntriesWithFilter(filteredDiffSummaries)
  const activeChangeGroup = useActiveChangeGroup()

  const {selectedPathDigest} = useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()
  const allDiffSummaries = useDiffSummaries()
  const {loadMore: loadMoreDiffEntries} = useProgressiveDiffActions()
  const diffEntries = useDiffEntries()

  const totalVisibleEntries = virtualDiffEntries.length
  const shouldShowWhimsy = totalVisibleEntries >= 15

  const heightPerLine = currentUser.lineSpacing === 'compact' ? 20 : 25

  const estimateVirtualRowSize = useCallback(
    (index: number) => {
      const entry = virtualDiffEntries[index]
      if (!entry) return 0

      const linesChanged = allDiffSummaries[entry.path]?.linesChanged ?? 5
      return heightPerLine * linesChanged + 75
    },
    [virtualDiffEntries, allDiffSummaries, heightPerLine],
  )

  // Not sure if this is necessary or if its providing stability
  const getItemKey = useCallback(
    (index: number) => virtualDiffEntries[index]?.pathDigest ?? index,
    [virtualDiffEntries],
  )

  useEffect(() => {
    if (diffsParentRef.current) {
      setScrollMargin(diffsParentRef.current.offsetTop)
    }
  }, [])

  const virtualizer = useWindowVirtualizer({
    count: virtualDiffEntries.length,
    estimateSize: estimateVirtualRowSize,
    getItemKey,
    overscan: 5,
    scrollMargin,
    gap: 16,
  })

  const virtualItems = virtualizer.getVirtualItems()

  const firstUnloadedIndex = useMemo(() => {
    for (const item of virtualItems) {
      const entry = virtualDiffEntries[item.index]
      if (entry?.loadingStatus === 'NotLoaded') {
        return item.index
      }
    }
    return -1
  }, [virtualItems, virtualDiffEntries])

  // Load unloaded entries that are currently visible
  useEffect(() => {
    if (firstUnloadedIndex === -1) return

    const entry = virtualDiffEntries[firstUnloadedIndex]
    if (!entry) return

    const cleanup = scheduleLowPriorityUpdate(() => loadMoreDiffEntries(entry), {timeout: 100})
    return cleanup
  }, [firstUnloadedIndex, virtualDiffEntries, loadMoreDiffEntries])

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map(virtualRow => (
          <VirtualizedDiffRow
            key={virtualRow.key}
            virtualRow={virtualRow}
            virtualizedDiffEntry={virtualDiffEntries[virtualRow.index]}
            diffEntry={diffEntries[virtualDiffEntries[virtualRow.index]?.path ?? '']}
            diffSummary={allDiffSummaries[virtualDiffEntries[virtualRow.index]?.path ?? '']}
            scrollMargin={virtualizer.options.scrollMargin}
            measureElement={virtualizer.measureElement}
            basePath={basePath}
            currentUser={currentUser}
            headBranchName={headBranchName}
            headRepoName={headRepoName}
            headRepoOwner={headRepoOwner}
            pullRequestGlobalRelayId={pullRequestGlobalRelayId}
            repository={repository}
            selectedPathDigest={selectedPathDigest}
          />
        ))}
      </div>
      {shouldShowWhimsy && <PullRequestDiffsAnimation />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

try{ PullRequestVirtualizedDiffsList.displayName ||= 'PullRequestVirtualizedDiffsList' } catch {}